import { useCallback, useEffect, useRef, useState } from "react";
import {
  MAX_RECORDING_MS,
  type VoiceInput,
  type VoiceState,
} from "./voice-input";

const TICK_MS = 1_000;

// TypeScript 5.5's lib.dom ships the result types but neither the constructor nor the events, so
// only the surface this hook touches is declared rather than pulling in a dependency for a spike.
type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
};

type SpeechRecognitionEventLike = {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

const recognitionConstructor = (): SpeechRecognitionConstructor | undefined => {
  const candidate = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return candidate.SpeechRecognition ?? candidate.webkitSpeechRecognition;
};

const transcriptOf = (event: SpeechRecognitionEventLike): string => {
  let said = "";
  for (let index = 0; index < event.results.length; index += 1) {
    const alternative = event.results[index]?.[0];
    if (alternative) said += alternative.transcript;
  }
  return said.trim();
};

// The spec's error codes, mapped to what the clinician can do about each. "aborted" is our own
// cancel and must stay silent, or every cancel would show a failure the clinician did not cause.
const ERROR_MESSAGES: Record<string, string> = {
  "audio-capture": "Could not use the microphone. Type it instead?",
  network: "Could not reach the speech service. Type it instead?",
  "no-speech": "Didn't catch that. Try again, or type it instead.",
  "language-not-supported":
    "This browser can't recognise that language. Type it instead?",
  "service-not-allowed":
    "This browser won't allow speech recognition here. Type it instead?",
};

// Unlike the agent engine this sends the audio to the browser vendor — Google on Chrome, Apple on
// Safari — under their terms rather than ours. Anything that widens who can reach it is a data
// residency change, not a UI one.
export const useWebSpeechInput = (): VoiceInput => {
  const [supported] = useState(() => recognitionConstructor() !== undefined);
  const [state, setState] = useState<VoiceState>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [message, setMessage] = useState<string>();
  const [transcript, setTranscript] = useState<string>();

  const recognitionRef = useRef<SpeechRecognitionLike>();
  // Same reasoning as the agent recorder: recognition events arrive after the attempt they belong
  // to may have been cancelled, and a result from an abandoned attempt must not reach the composer.
  const attemptRef = useRef(0);

  const release = useCallback(() => {
    attemptRef.current += 1;
    const recognition = recognitionRef.current;
    recognitionRef.current = undefined;
    recognition?.abort();
  }, []);

  useEffect(() => release, [release]);

  const start = useCallback(() => {
    const Recognition = recognitionConstructor();
    if (!Recognition) return;

    attemptRef.current += 1;
    const attempt = attemptRef.current;
    const current = () => attemptRef.current === attempt;
    setState("starting");
    setMessage(undefined);
    setElapsedMs(0);

    let recognition: SpeechRecognitionLike;
    try {
      recognition = new Recognition();
    } catch {
      // A present constructor is not a working one, and "starting" renders neither a mic nor a
      // text field — so an unhandled throw here leaves no way to answer at all.
      setState("error");
      setMessage("Could not start listening. Type it instead?");
      return;
    }
    recognition.lang = navigator.language || "en-GB";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    let heard = false;

    recognition.onstart = () => {
      if (!current()) return;
      setState("recording");
    };

    recognition.onresult = (event) => {
      if (!current()) return;
      const said = transcriptOf(event);
      if (!said) return;
      heard = true;
      setState("idle");
      setMessage(undefined);
      setTranscript(said);
    };

    recognition.onerror = (event) => {
      if (!current()) return;
      if (event.error === "aborted") return;
      heard = true;
      if (event.error === "not-allowed") {
        setState("denied");
        return;
      }
      setState("error");
      setMessage(
        ERROR_MESSAGES[event.error] ??
          "Could not use the microphone. Type it instead?",
      );
    };

    // Recognition can end having produced neither a result nor an error — a stop with nothing
    // recognised. Without this the composer would sit in "recording" with no way back.
    recognition.onend = () => {
      // The attempt check comes first: a cancelled recognition ending after its replacement has
      // installed a ref would otherwise erase it, leaving Stop and the cap with nothing to stop.
      if (!current()) return;
      recognitionRef.current = undefined;
      if (heard) return;
      setState("error");
      setMessage("Didn't catch that. Try again, or type it instead.");
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      if (!current()) return;
      setState("error");
      setMessage("Could not start listening. Type it instead?");
    }
  }, []);

  const stop = useCallback(() => {
    const recognition = recognitionRef.current;
    recognitionRef.current = undefined;
    recognition?.stop();
  }, []);

  const cancel = useCallback(() => {
    release();
    setState("idle");
    setMessage(undefined);
  }, [release]);

  const clearTranscript = useCallback(() => setTranscript(undefined), []);

  useEffect(() => {
    if (state !== "recording") return;
    const attempt = attemptRef.current;
    const startedAt = performance.now();
    const timer = setInterval(() => {
      if (attemptRef.current !== attempt) return;
      setElapsedMs(performance.now() - startedAt);
    }, TICK_MS);
    // A one-shot deadline rather than a comparison inside the tick: the cap would otherwise depend
    // on the interval firing punctually, and a delayed tick would let the take run past the limit.
    const cap = setTimeout(() => {
      if (attemptRef.current !== attempt) return;
      stop();
    }, MAX_RECORDING_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(cap);
    };
  }, [state, stop]);

  return {
    available: supported,
    state,
    elapsedMs,
    message,
    transcript,
    start,
    stop,
    cancel,
    clearTranscript,
  };
};
